import { useCallback, useEffect, useRef } from 'react';
import type { ChannelStateChange } from 'ably';
import { useChannelStateListener } from 'ably/react';

/**
 * How long the channel must stay quiet before a fresh attach's rewind backlog counts as drained.
 * Every message observed while draining re-arms this deadline. Messages arriving after a quiet
 * interval, or after the absolute cap below, are classified as live. Do not go below ~300ms: slow
 * networks can split one backlog into two frame gaps.
 */
export const REWIND_QUIESCENCE_MS = 500;

/** Absolute cap on suppression after an attach, in case the channel never goes quiet. */
export const REWIND_DRAIN_MAX_MS = 8000;

/**
 * Tracks Ably's own attach/replay boundary so we can tell history from live events without
 * comparing an Ably server timestamp against the client wall clock - that comparison misfires on
 * clock skew or reconnect and silently dropped genuinely-live notifications (SCAL-9093).
 *
 * Returns an `isReplaying()` callback that is `true` while a fresh attach's rewind history is
 * draining and `false` once live; each call while draining re-arms the quiescence deadline, so
 * callers invoke it once per received message. A resume (`resumed === true`) carries only messages
 * missed while offline, and an attach reporting `hasBacklog === false` has nothing to replay, so
 * both go live immediately - but `hasBacklog` is optional in ably's typings, so `undefined` stays
 * conservative and drains. A mount-time drain guarantees we never stay stuck suppressing if the
 * attach fired before this listener registered.
 */
export function useReplayWindow(channelName: string) {
  const replayingRef = useRef(true);
  const quiescenceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const goLive = useCallback(() => {
    clearTimeout(quiescenceTimerRef.current);
    clearTimeout(capTimerRef.current);
    replayingRef.current = false;
  }, []);

  const beginDrain = useCallback(() => {
    replayingRef.current = true;
    clearTimeout(quiescenceTimerRef.current);
    quiescenceTimerRef.current = setTimeout(goLive, REWIND_QUIESCENCE_MS);
    clearTimeout(capTimerRef.current);
    capTimerRef.current = setTimeout(goLive, REWIND_DRAIN_MAX_MS);
  }, [goLive]);

  const onStateChange = useCallback(
    (stateChange: ChannelStateChange) => {
      if (stateChange.current !== 'attached') {
        return;
      }
      if (stateChange.resumed || stateChange.hasBacklog === false) {
        goLive();
      } else {
        beginDrain();
      }
    },
    [goLive, beginDrain]
  );
  useChannelStateListener(channelName, onStateChange);

  useEffect(() => {
    beginDrain();
    return goLive;
  }, [beginDrain, goLive]);

  return useCallback(() => {
    if (!replayingRef.current) {
      return false;
    }
    clearTimeout(quiescenceTimerRef.current);
    quiescenceTimerRef.current = setTimeout(goLive, REWIND_QUIESCENCE_MS);
    return true;
  }, [goLive]);
}
